import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/** Marks a grid item held back so the last row stays flush. See `settleRows`. */
const ROW_OVERFLOW_ATTRIBUTE = 'data-row-overflow';

/** Sub-pixel slack when comparing a row's edge against the grid's, in px. */
const FLUSH_TOLERANCE = 1;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#deferInitialFetch();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Prefetches the adjacent pages eagerly, but only once first render is out of
   * the way: after `load` and while the main thread is idle. It stays eager
   * (not "fetch on scroll") so `#renderNextPage`/`#renderPreviousPage` usually
   * find a warm cache; if a shopper acts before this fires, those methods fall
   * back to fetching on demand themselves.
   */
  #deferInitialFetch() {
    const schedule = () =>
      requestIdleCallback(() => {
        this.#fetchPage('next');
        this.#fetchPage('previous');
      });

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    const { usesManualPagination } = this;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements. Under manual pagination neither direction
    // loads on its own: the shopper asked for a button, so scrolling past either
    // end of the list must not append or prepend a page behind their back.
    if (viewMorePrevious && !usesManualPagination) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext && !usesManualPagination) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Whether the merchant chose the manual "Load more" button over infinite scroll.
   *
   * @returns {boolean}
   */
  get usesManualPagination() {
    return this.getAttribute('pagination-type') === 'load_more';
  }

  /**
   * Reflects a newly appended or prepended page in the address bar.
   *
   * Infinite scroll owns the URL: the shopper never asked for a page, so `?page=N`
   * is the only record of how far they got, and each page is a history entry.
   *
   * Manual pagination only *replaces* the current entry. Every click is deliberate,
   * and a `pushState` per click buries the page they arrived from one more entry
   * deep, so Back out of the collection (or back from a product) would walk them
   * through `?page=N-1`, `?page=N-2`... instead of leaving. The address bar still
   * shows the page just loaded, matching the Load more link's `href` (17tqamervex).
   * Note that a reload then renders `?page=N` alone, not the products before it.
   *
   * @param {URL} url
   */
  #reflectPageInUrl(url) {
    if (this.usesManualPagination) {
      history.replaceState(history.state, '', url.toString());
      return;
    }

    history.pushState('', '', url.toString());
  }

  async renderNextPage() {
    await this.#renderNextPage();
  }

  /**
   * Hook for a fetched page's items before they are inserted.
   *
   * @param {NodeListOf<Element>} items
   */
  prepareItems(items) {}

  /**
   * Trims the grid so its last row is flush, holding back trailing items until a
   * later page — or a layout change — can complete the row.
   *
   * A page is a fixed number of products (24), but the rendered column count
   * varies: the merchant's `desktop_columns`, +1 from the above-the-fold column
   * bump, 7 in the zoom-out view, 2 or 1 on mobile, and promo tiles spanning
   * multiple cells. So a page rarely divides evenly into rows. Rather than ask
   * the server for a column-aware page size (impossible — Liquid can't read the
   * Section Rendering API's query params), the shortfall is hidden until the
   * next page arrives to fill it.
   *
   * Only trailing items are ever hidden, so the held-back set stays a
   * contiguous tail and document order needs no bookkeeping.
   *
   * `sections/main-collection.liquid`'s `settleGridRowsEarly()` mirrors this
   * geometric logic in a synchronous inline script, so the first-loaded page
   * is already flush before this module — fetched `fetchpriority="low"` —
   * has arrived and upgraded `<results-list>`. This call still runs once it
   * does: it reveals whatever that script held back and re-settles for real,
   * so the two never disagree for long. Keep both in sync.
   *
   * @param {{ floor?: number }} [options] - `floor` is the number of leading items that
   *   must stay visible; items already visible before an append were flush, so they are
   *   never hidden to settle the items that followed them.
   */
  settleRows({ floor = 0 } = {}) {
    const { grid } = this.refs;

    if (!grid) return;

    // Reveal anything held back previously: the items appended since are what
    // complete the row it was short of.
    const heldBack = grid.querySelectorAll(`:scope > [${ROW_OVERFLOW_ATTRIBUTE}]`);

    for (const item of heldBack) {
      if (!(item instanceof HTMLElement)) continue;

      item.removeAttribute(ROW_OVERFLOW_ATTRIBUTE);
      this.#reveal(item);
    }

    // On the last page the remainder is real rather than a shortfall, so there
    // is nothing to wait for — leave it be.
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastCard = cards[cards.length - 1];
    const lastCardPage = lastCard instanceof HTMLElement ? Number(lastCard.dataset.page) : NaN;

    if (!lastCardPage || lastCardPage >= Number(grid.dataset.lastPage)) return;

    const visible = this.#visibleItems(grid);
    const edges = this.#gridInlineEdges(grid);

    /** @type {HTMLElement[]} */
    const hidden = [];
    const minVisible = Math.max(floor, 1);
    let flush = this.#lastRowIsFlush(grid, visible, edges);

    // Hide the last visible item and re-measure, until the row is flush or we
    // reach the items that were already settled.
    while (!flush && visible.length > minVisible) {
      const item = visible.pop();

      if (!item) break;

      item.hidden = true;
      item.setAttribute(ROW_OVERFLOW_ATTRIBUTE, '');
      hidden.push(item);
      flush = this.#lastRowIsFlush(grid, visible, edges);
    }

    // Fail open: if flushness was never reached, hiding products bought us
    // nothing, so put them all back.
    if (!flush) {
      for (const item of hidden) {
        this.#reveal(item);
        item.removeAttribute(ROW_OVERFLOW_ATTRIBUTE);
      }
    }
  }

  /**
   * Shows a held-back item with its card sliders already scrollable.
   *
   * A slider only becomes a scroll container once slideshow.js's observer marks it
   * `in-viewport`, which a `display: none` item never is. Revealed in place, it would
   * switch while on screen, and Safari paints the new scroll layer blank until a hover
   * or page scroll. Marking it first means it renders scrollable from its first frame.
   * An item revealed off screen keeps the attribute until it next leaves the viewport,
   * which only costs that handful of sliders their idle `overflow: hidden`.
   *
   * @param {HTMLElement} item
   */
  #reveal(item) {
    for (const slideshow of item.querySelectorAll('slideshow-component')) {
      slideshow.setAttribute('in-viewport', '');
    }
    item.hidden = false;
  }

  /**
   * The grid's items that are currently on screen, in document order.
   *
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  #visibleItems(grid) {
    return /** @type {HTMLElement[]} */ (
      Array.from(grid.children).filter((child) => child instanceof HTMLElement && !child.hidden)
    );
  }

  /**
   * Whether the grid's last visible row fills all of its columns.
   *
   * Measured geometrically rather than counted, so spanning promo tiles, the
   * organic layout's wide cards, `auto-fill` tracks and every breakpoint are all
   * covered by the same test. Only inline edges are compared, so images loading
   * in later can't skew the result.
   *
   * @param {HTMLElement} grid
   * @param {HTMLElement[]} visible - The grid's visible children, in document order.
   * @returns {boolean}
   */
  #lastRowIsFlush(grid, visible, edges = this.#gridInlineEdges(grid)) {
    const last = visible[visible.length - 1];

    if (!last) return true;

    const lastRect = last.getBoundingClientRect();

    return edges.rtl ? lastRect.left <= edges.left + FLUSH_TOLERANCE : lastRect.right >= edges.right - FLUSH_TOLERANCE;
  }

  /**
   * The grid's inline edges, read once so the hide-and-remeasure loop in
   * `settleRows` forces one layout per hidden item rather than two. Hiding a
   * trailing item changes the grid's height, never its inline edges, so these
   * stay valid for the whole loop.
   *
   * @param {HTMLElement} grid
   * @returns {{ left: number, right: number, rtl: boolean }}
   */
  #gridInlineEdges(grid) {
    // `.product-grid` has no padding, so its border box is its content box.
    const { left, right } = grid.getBoundingClientRect();

    return { left, right, rtl: getComputedStyle(grid).direction === 'rtl' };
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    // Items already on screen were settled by the previous append, so they are
    // off limits when trimming this one back to a flush row.
    const settledCount = this.#visibleItems(grid).length;

    this.prepareItems(nextPageItemElements);
    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    this.settleRows({ floor: settledCount });

    this.#reflectPageInUrl(nextPage.url);

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    this.prepareItems(previousPageItemElements);
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Prepending changes how many cells precede the tail, which can leave the
    // last row short. Re-settle it, but without hiding any more than is already
    // held back — a prepend is no reason to take products off screen.
    this.settleRows({ floor: this.#visibleItems(grid).length });

    this.#reflectPageInUrl(previousPage.url);

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    // iOS Safari resolves this document's protocol-relative image URLs against about:blank
    // once these nodes are adopted into the live grid below - give it a real base first.
    parsedPage.head.append(Object.assign(parsedPage.createElement('base'), { href: window.location.href }));
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
